import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {Drawer, DrawerContent, DrawerDescription, DrawerTitle} from '../../drawer';
import {cn} from '../../lib/cn';

type SelectMenuPanelProps = {
  isOpen: boolean;
  isMobile: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  drawerClassName?: string;
  drawerContentClassName?: string;
  children: React.ReactNode;
};

function SelectMenuPanel({
  isOpen,
  isMobile,
  onClose,
  title,
  description,
  className,
  drawerClassName,
  drawerContentClassName,
  children,
}: SelectMenuPanelProps) {
  const {t} = useTranslation();
  if (!isOpen) return null;

  if (isMobile) {
    const fallbackTitle = t('select_option');
    const titleText =
      typeof title === 'string' || typeof title === 'number'
        ? String(title)
        : fallbackTitle;
    const descriptionText =
      typeof description === 'string' || typeof description === 'number'
        ? String(description)
        : titleText;

    return (
      <Drawer
        open={isOpen}
        onOpenChange={next => {
          if (!next) onClose();
        }}
      >
        <DrawerContent
          data-select-menu=""
          onClose={onClose}
          lockScroll={false}
          className={cn('max-h-[calc(100vh-1rem)]', drawerClassName)}
        >
          <DrawerTitle className="sr-only">{titleText}</DrawerTitle>
          <DrawerDescription className="sr-only">{descriptionText}</DrawerDescription>
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col px-1 pb-4 pt-1',
              drawerContentClassName,
            )}
          >
            {children}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <div
      data-select-menu=""
      className={cn(
        'absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-lg border border-[var(--chekin-color-gray-3)] bg-white shadow-[0_4px_8px_-2px_rgba(22,22,67,0.06),0_16px_32px_-12px_rgba(33,72,255,0.18)] ring-1 ring-black/[0.02] animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 duration-150',
        className,
      )}
    >
      {children}
    </div>
  );
}

export {SelectMenuPanel};
export type {SelectMenuPanelProps};
